/**
 * Communication Tutor Can-Do + progress APIs (gateway /v1/tutor).
 */
import { createHttpClient } from '../../api/http';
import { serviceUrls } from '../../config/serviceUrls';
import {
  fetchCanDosBundle,
  type CanDoItem,
  type CommunicationTopicMeta,
} from '../../modules/tutor/api/tutor.client';

const TUTOR_BASE = import.meta.env.VITE_AI_TUTOR_BASE_URL || serviceUrls.gateway;
const client = createHttpClient(TUTOR_BASE);
const PREFIX = TUTOR_BASE.includes('ai-tutor') ? '/tutor' : '/v1/tutor';

/** Mongo lesson slot for Communication Tutor skills (topics + can-dos). */
export const COMMUNICATION_SKILLS_LESSON_NO = 0;

export type PerformanceScores = {
  fluency: number;
  accuracy: number;
  appropriateness: number;
};

export type StatementForUi = {
  can_do_id: string;
  statement_en: string;
  statement_ta: string;
};

export type CommunicationTurnResponse = {
  ok: boolean;
  conversation_id: string;
  can_do: { auto_ids: string[]; manual_ids: string[] };
  performance: PerformanceScores;
  statements_for_ui: StatementForUi[];
};

export type SkillProgressEntry = {
  can_do_id: string;
  statement: string;
  avg_performance: PerformanceScores;
};

export type StudentProgressResponse = {
  user_id: string;
  skills: SkillProgressEntry[];
};

export type CommunicationLogRow = {
  id?: string;
  conversation_id: string;
  created_at?: string;
  grade?: number;
  lesson_no?: number;
  session_id?: string | null;
  student_message?: string;
  ai_response?: string;
  can_do?: { auto_ids: string[]; manual_ids: string[] };
  performance?: PerformanceScores;
  effective_can_do_ids: string[];
  rubric_l1_l4?: Record<string, { level: string; score: number }>;
  sentence_corrections?: {
    original_fragment: string;
    suggested_fragment: string;
    reason_en?: string;
    reason_ta?: string;
    severity: string;
  }[];
  highlight_spans?: { start: number; end: number; kind: string }[];
  encouragement_snippet?: { ta?: string; en?: string };
  audio_uri?: string;
};

export async function getCanDos(grade: number, lessonNo: number): Promise<CanDoItem[]> {
  const { can_dos } = await fetchCanDosBundle(grade, lessonNo);
  return can_dos;
}

export async function getCanDosWithTopics(
  grade: number,
  lessonNo: number
): Promise<{ canDos: CanDoItem[]; communicationTopics: CommunicationTopicMeta[] }> {
  const { can_dos, communication_topics } = await fetchCanDosBundle(grade, lessonNo);
  return { canDos: can_dos, communicationTopics: communication_topics };
}

export async function postCommunicationTurn(payload: {
  student_message: string;
  ai_response: string;
  grade: number;
  lesson_no: number;
  session_id?: string | null;
  /** When set, this Can-Do id is validated and prepended to auto tag results for the active practice topic. */
  practice_can_do_id?: string | null;
}): Promise<CommunicationTurnResponse> {
  const r = await client.post<CommunicationTurnResponse>(`${PREFIX}/communication-turn`, {
    student_message: payload.student_message,
    ai_response: payload.ai_response,
    grade: payload.grade,
    lesson_no: payload.lesson_no,
    session_id: payload.session_id ?? undefined,
    practice_can_do_id: payload.practice_can_do_id ?? undefined,
  });
  return r.data;
}

export async function getStudentProgress(userId: string): Promise<StudentProgressResponse> {
  const r = await client.get<StudentProgressResponse>(`${PREFIX}/student-progress/${userId}`);
  return r.data;
}

export async function getStudentProgressMe(): Promise<StudentProgressResponse> {
  const r = await client.get<StudentProgressResponse>(`${PREFIX}/student-progress/me`);
  return r.data;
}

export async function overrideCanDo(conversationId: string, manualIds: string[]): Promise<void> {
  await client.put(`${PREFIX}/conversations/${conversationId}/can-do`, { manual_ids: manualIds });
}

export async function listStudentCommunicationLogs(
  userId: string,
  params?: { lesson_no?: number; limit?: number }
): Promise<{ ok: boolean; items: CommunicationLogRow[] }> {
  const r = await client.get<{ ok: boolean; items: CommunicationLogRow[] }>(
    `${PREFIX}/students/${userId}/communication-logs`,
    { params: { lesson_no: params?.lesson_no, limit: params?.limit ?? 100 } }
  );
  return r.data;
}
